// ============================================================================
// "My markets / All TCS" on the Subcontractors hub — remembered per person.
//
// ⛔ NO TABLE AND NO COLUMN. The prompt named "the density setting" as the
// precedent; there is no density setting in src/. The real precedent is the
// per-person remembered choice already used three times — useFacilityScope
// (My Accounts' mine / all), useTimeHoursScope and theme.tsx — a localStorage
// key namespaced by employee id. This is the same shape with its own key, so
// flipping it here does not flip My Accounts.
//
// Default: My markets when the person manages buildings; All TCS when they do
// not (HR, executives) — the never-a-blank-screen guarantee useFacilityScope
// gives a new hire.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../shared/auth';
import type { HubScope } from './use-hub-data';

const STORAGE_PREFIX = 'tcs.subsHubScope.';

function readStored(eid: string | null): HubScope | null {
  if (!eid) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + eid);
    return raw === 'mine' || raw === 'all' ? raw : null;
  } catch {
    return null;
  }
}

function writeStored(eid: string | null, scope: HubScope): void {
  if (!eid) return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + eid, scope);
  } catch {
    /* private browsing / quota — the choice just falls back to the default */
  }
}

export function useHubScope(hasMarkets: boolean): { scope: HubScope; setScope: (s: HubScope) => void } {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [stored, setStored] = useState<HubScope | null>(() => readStored(eid));
  useEffect(() => setStored(readStored(eid)), [eid]);
  const setScope = useCallback(
    (s: HubScope) => {
      setStored(s);
      writeStored(eid, s);
    },
    [eid],
  );
  const scope: HubScope = !hasMarkets ? 'all' : (stored ?? 'mine');
  return { scope, setScope };
}
